import React, { useContext, useState, useEffect } from 'react';
import { Button, ErrorMessage, LoadingSpinner, Modal, LoginModal } from 'components';
import { LocalContext, FirebaseContext } from 'context';
import Autocomplete from 'react-google-autocomplete';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { fadeInAndOutVariants } from 'styles';
import { includesSurname } from 'utils';
import {
  FormContainer,
  FormInput,
  FormInputLabel,
  FormChecks,
  FormCheckbox,
  FormTerms
} from '../FormComponents';

const resetForm = () => ({
  name: '',
  email: '',
  institutionOrCompany: '',
  awardCPDPoints: false,
  confirmTermsAndConditions: false,
  referringUrl: typeof window !== 'undefined' && JSON.parse(window.localStorage.getItem('referringUrlRetina'))?.data
});

const RegistrationForm = () => {
  const { theme, setANewUserHasRegisteredForAnEvent } = useContext(LocalContext);
  const { firebase } = useContext(FirebaseContext);
  const [formValues, setFormValues] = useState(resetForm());
  const [errorMessage, setErrorMessage] = useState('');
  const [emailAddressAlreadyInUse, setEmailAddressAlreadyInUse] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState({
    submitting: false,
    submitted: false
  });
  const [
    disableSubmitButtonWhileCheckingEmailAddress,
    setDisableSubmitButtonWhileCheckingInputs
  ] = useState(false);
  let isMounted = true;
  let debounce;
  const debounceTimeout = 750;

  useEffect(() => {
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const { name } = formValues;
    if (name) {
      setDisableSubmitButtonWhileCheckingInputs(true);
      debounce = setTimeout(() => {
        if (!includesSurname(name)) {
          setErrorMessage('Please include a surname');
        }
        setDisableSubmitButtonWhileCheckingInputs(false);
      }, debounceTimeout);
    }
    return () => clearTimeout(debounce);
  }, [formValues.name]);

  useEffect(() => {
    const { email } = formValues;
    if (email) {
      setDisableSubmitButtonWhileCheckingInputs(true);
      debounce = setTimeout(async () => {
        try {
          const result = await firebase.checkIfUserAlreadyExists({ email });
          if (!result.empty) {
            throw new Error(
              "This email address already belongs to a Retina 2021 account. Click 'Login' above if it belongs to you."
            );
          } else if (emailAddressAlreadyInUse) {
            setErrorMessage('');
            setEmailAddressAlreadyInUse(false);
          }
        } catch (error) {
          setErrorMessage(error.message);
          setEmailAddressAlreadyInUse(true);
        }
        setDisableSubmitButtonWhileCheckingInputs(false);
      }, debounceTimeout);
    }
    return () => clearTimeout(debounce);
  }, [formValues.email]);

  const handleInputChange = (e) => {
    e.persist();
    const { name, type, checked, value } = e.target;
    if (errorMessage && !emailAddressAlreadyInUse && name !== 'email') {
      setErrorMessage('');
    }
    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleWorkplaceAutocompleteSelection = (res) => {
    const { name } = res;
    if (errorMessage && !emailAddressAlreadyInUse) {
      setErrorMessage('');
    }
    setFormValues((currentValues) => ({
      ...currentValues,
      institutionOrCompany: name.split(',')[0]
    }));
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (errorMessage || submissionStatus.submitting) {
      return;
    }

    const { name, confirmTermsAndConditions, email, institutionOrCompany } = formValues;

    if (!includesSurname(name)) {
      setErrorMessage('Please include a surname');
      return;
    }

    if (!email) {
      setErrorMessage('Please include an email address');
      return;
    }

    if (emailAddressAlreadyInUse) {
      setErrorMessage(
        "This email address already belongs to a Retina 2021 account. Click 'Login' above if it belongs to you."
      );
      return;
    }

    if (!institutionOrCompany) {
      setErrorMessage('Please include your institution/company name');
      return;
    }

    if (!confirmTermsAndConditions) {
      setErrorMessage('You must agree to our Terms & Conditions to register.');
      setSubmissionStatus({
        submitting: false,
        submitted: false
      });
      return;
    }

    if (
      confirmTermsAndConditions
    ) {
      try {
        setSubmissionStatus({
          submitting: true,
          submitted: false
        });

        const actionCodeSettings = {
          url: `${window.location.origin}`,
          handleCodeInApp: true
        };

        const { data } = await firebase.sendSignInWithMagicLinkEmail({
          firstName: name.split(' ')[0],
          email,
          actionCodeSettings
        });

        if (data.Message === 'OK') {
          setSubmissionStatus({
            submitting: false,
            submitted: true
          });
          window.localStorage.setItem(
            'newUserRetina',
            JSON.stringify({
              ...formValues,
              actionCodeSettings
            })
          );
          setANewUserHasRegisteredForAnEvent(true);
        } else {
          setSubmissionStatus({
            submitting: false,
            submitted: false
          });
          setErrorMessage('Error sending email. Please try again');
        }

        setFormValues(resetForm());
      } catch (error) {
        console.error(error);

        if (isMounted) {
          setErrorMessage(error.message);
        }

        setSubmissionStatus({
          submitting: false,
          submitted: false
        });
      }
    }
  }

  return (
    <FormContainer onSubmit={(e) => handleSubmit(e)} theme={theme}>
      <div
        style={{
          fontSize: '1.75rem',
          fontWeight: 600,
          lineHeight: '1.75rem',
          marginBottom: '0.55rem',
          textAlign: 'center'
        }}>
        Register Here
      </div>
      <div
        style={{
          color: `#fff`,
          display: 'flex',
          fontSize: '0.875rem',
          justifyContent: 'center',
          marginBottom: '2.125em'
        }}>
        Already have an account?&nbsp;
        <Modal
          trigger={
            <span
              style={{
                fontSize: '0.875rem',
                textAlign: 'center',
                marginBottom: '1.5em',
                textDecoration: 'underline'
              }}>
              Click here!
            </span>
          }
          disableCloseOnClickOutside
          modalContent={<LoginModal />}
        />
      </div>
      <FormInputLabel hide htmlFor="name">
        Full Name
      </FormInputLabel>
      <FormInput
        id="name"
        name="name"
        onChange={handleInputChange}
        placeholder="Full Name*"
        type="text"
        value={formValues.name}
        required
        theme={theme}
      />
      <FormInputLabel hide htmlFor="email">
        Email
      </FormInputLabel>
      <FormInput
        id="email"
        name="email"
        onChange={handleInputChange}
        placeholder="Email*"
        type="email"
        value={formValues.email}
        required
        theme={theme}
      />
      <FormInputLabel hide htmlFor="name">
        Institution or Company Name
      </FormInputLabel>
      <FormWorkplaceAutoComplete
        id="institutionOrCompany"
        name="institutionOrCompany"
        apiKey={process.env.GATSBY_GOOGLE_MAPS_API_KEY}
        options={{
          types: ['establishment'],
          fields: ['name']
        }}
        onChange={handleInputChange}
        onPlaceSelected={handleWorkplaceAutocompleteSelection}
        placeholder="Institution or Company Name*"
        type="select"
        required
        value={formValues.institutionOrCompany}
      />
      {/* <FormChecks style={{ marginBottom: '1rem' }}>
        <FormInputLabel hide htmlFor="awardCPDPoints">
          Confirm CPD Points
        </FormInputLabel>
        <FormCheckbox
          checked={formValues.awardCPDPoints}
          id="awardCPDPoints"
          name="awardCPDPoints"
          onChange={handleInputChange}
          theme={theme}
          type="checkbox"
        />
        <FormTerms style={{
            marginTop: '0.625em'
          }}>
          I would like to be awarded CPD points for attending this live event.
        </FormTerms>
      </FormChecks> */}
      <FormChecks style={{ marginBottom: '0.9rem' }}>
        <FormInputLabel hide htmlFor="confirmTermsAndConditions">
          Confirm Terms And Conditions
        </FormInputLabel>
        <FormCheckbox
          checked={formValues.confirmTermsAndConditions}
          id="confirmTermsAndConditions"
          name="confirmTermsAndConditions"
          onChange={handleInputChange}
          required
          theme={theme}
          type="checkbox"
        />
        <FormTerms>
          By supplying my contact information, I agree to Terms & Conditions on the Retina 2021 website. See our Privacy Policy for more details.
        </FormTerms>
      </FormChecks>
      <ErrorMessage errorMessage={errorMessage} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '1.25rem'
        }}>
        {submissionStatus.submitted ? (
          <div
            style={{
              textAlign: 'center',
              marginBottom: '0.5rem',
              color: theme.fg,
              width: '90%'
            }}>
            Check your inbox to verify your email address and complete your registration. Can&apos;t
            find our email? Be sure to check your junk!
          </div>
        ) : (
          <Button
            type="submit"
            width="7.5rem"
            disabled={emailAddressAlreadyInUse || disableSubmitButtonWhileCheckingEmailAddress}
            style={{
              alignItems: 'center',
              alignSelf: 'center',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative'
            }}>
            {
              <AnimatePresence>
                {submissionStatus.submitting && (
                  <LoadingSpinner style={{ width: '2rem', color: '#fff', position: 'absolute' }} />
                )}
              </AnimatePresence>
            }
            {
              <AnimatePresence>
                {!submissionStatus.submitting && (
                  <motion.span
                    variants={fadeInAndOutVariants()}
                    whileTap={{ scale: 0.95 }}
                    initial="initial"
                    animate="animate"
                    exit="exit">
                    Submit
                  </motion.span>
                )}
              </AnimatePresence>
            }
          </Button>
        )}
      </div>
    </FormContainer>
  );
};

const FormWorkplaceAutoComplete = styled(Autocomplete)`
  align-items: center;
  background: transparent;
  border: 0.063rem solid #fff;
  color: white !important;
  display: flex;
  font-family: 'Noto Sans', sans-serif;
  font-size: 1rem;
  font-weight: bold;
  height: 3.75rem;
  letter-spacing: 0.031rem;
  margin-bottom: 1rem;
  outline: none;
  padding: 0 1.25rem;

  ::placeholder {
    font-family: 'Noto Sans', sans-serif;
    font-style: italic;
    font-weight: bold;
    font-size: 1rem;
    color: white !important;
  }
`;

export default RegistrationForm;
